import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  styled,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { ModalLoading } from "../../../../../../common/components/modal_loading";
import {
  isNotNullOrUndefined,
  normalizeFileName,
} from "../../../../../../common/utils/utils";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { GEDDocumento } from "../../../../../../common/domain/models/ged_documento";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  GedDocumentoForm,
  GedDocumentoSchema,
} from "../../../schemas/GED_externos/ged_documento";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { DefaultFormDatePickerInput } from "../../../../../../common/components/default_form_date_picker_input";
import { DefaultFormRadioGroupInput } from "../../../../../../common/components/default_form_radio_group_input";

import DefaultFormInput from "../../../../../../common/components/default_form_input";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";
import dayjs from "dayjs";

/**
 * VisuallyHiddenInput
 */

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

/**
 * CadastroDocumentoModal
 */

type Props = {
  open: boolean;
  saving: boolean;
  selectedDocumento: GEDDocumento | null;
  centroCustoDropdown: CentroCustoDropDown[];
  onClose: () => void;
  onSubmit: (form: GedDocumentoForm) => void;
  onDownloadFile: () => void;
  onDownloadFilePdf: () => void;
  onChangeHistory: () => void;
};

export const CadastroDocumentoModal = ({
  open,
  saving,
  selectedDocumento,
  centroCustoDropdown,
  onClose,
  onSubmit,
  onDownloadFile,
  onDownloadFilePdf,
  onChangeHistory,
}: Props) => {
  const hookForm = useForm<GedDocumentoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(GedDocumentoSchema),
  });

  const [gedDocumento, setGedDocumento] = useState(selectedDocumento);

  const statusOptions = useMemo(() => {
    return [
      {
        id: 2,
        label: "Atual",
        style: {
          backgroundColor: "#45b6af",
          color: "white",
          padding: ".2rem .4rem",
          fontSize: "0.85rem",
          fontWeight: 700,
          textShadow:
            "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000",
          borderRadius: "2px",
        },
      },
      {
        id: 1,
        label: "Obsoleto",
        style: {
          backgroundColor: "#f3565d",
          color: "white",
          padding: ".2rem .4rem",
          fontSize: "0.85rem",
          fontWeight: 700,
          textShadow:
            "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000",
          borderRadius: "2px",
        },
      },
    ];
  }, []);

  useEffect(() => {
    if (open) {
      if (selectedDocumento) {
        hookForm.setValue(
          "statusArquivo",
          selectedDocumento.obsoleto === true ? 1 : 2,
        );
        hookForm.setValue("arquivo", selectedDocumento.descricao ?? "");
        hookForm.setValue("versao", selectedDocumento.versao);
        hookForm.setValue("centroCusto", selectedDocumento.idCentroCusto);
        hookForm.setValue(
          "conferirAtualizacao",
          selectedDocumento.conferirAtualizacao,
        );
        hookForm.setValue("local1", selectedDocumento.local1);
        hookForm.setValue("local2", selectedDocumento.local2);
        hookForm.setValue("assunto", selectedDocumento.assunto);
        hookForm.setValue("ondeConsultar", selectedDocumento.ondeConsultar);
        hookForm.setValue(
          "dataVencimento",
          selectedDocumento.dataVencimento !== null
            ? dayjs(selectedDocumento.dataVencimento).local().toDate()
            : null,
        );
        hookForm.setValue(
          "dataPublicacao",
          selectedDocumento.dataPublicacao !== null
            ? dayjs(selectedDocumento.dataPublicacao).local().toDate()
            : null,
        );
        hookForm.setValue(
          "dataVigencia",
          selectedDocumento.dataVigencia !== null
            ? dayjs(selectedDocumento.dataVigencia).local().toDate()
            : null,
        );
        hookForm.setValue("file", null);
        hookForm.setValue("filePdf", null);
        hookForm.setValue("limparArquivo", false);
        hookForm.setValue("limparArquivoPdf", false);
      } else {
        hookForm.reset({ statusArquivo: 2 });
      }
      setGedDocumento(selectedDocumento);
    }
  }, [open, selectedDocumento, hookForm]);

  const handleClose = useCallback(() => {
    if (!saving) onClose();
  }, [saving, onClose]);

  const handleSubmit = useCallback(
    (form: any) => {
      if (!saving) onSubmit(form);
    },
    [saving, onSubmit],
  );

  const handleDownloadFile = useCallback(() => {
    if (!saving) onDownloadFile();
  }, [saving, onDownloadFile]);

  const handleDownloadPdf = useCallback(() => {
    if (!saving) onDownloadFilePdf();
  }, [saving, onDownloadFilePdf]);

  const handleDeleteFile = useCallback(() => {
    if (window.confirm("Você deseja excluir este item?")) {
      hookForm.setValue("limparArquivo", true);
      setGedDocumento((doc) => {
        if (doc === null) return doc;
        return { ...doc, nome: null, arquivo: null };
      });
    }
  }, [hookForm]);

  const handleDeletePdf = useCallback(() => {
    if (window.confirm('Você deseja excluir este item?')) {
      hookForm.setValue('limparArquivoPdf', true);
      setGedDocumento(doc => {
        if (doc === null) return doc;
        return ({ ...doc, nomePdf: null, arquivoPdf: null });
      });
    }
  }, [
    hookForm,
  ]);

  const handleFileChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, onChange: (e: any) => void) => {
      if (!saving && e.target.files && e.target.files.length > 0) {
        const f = e.target.files.item(0);
        if (f) {
          hookForm.setValue("limparArquivo", false);
          hookForm.setValue("file", f);

          onChange(f);
        }
      }
    },
    [saving, hookForm],
  );

  const handlePdfChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, onChange: (e: any) => void) => {
      if (!saving && e.target.files && e.target.files.length > 0) {
        const f = e.target.files.item(0);
        if (f) {
          hookForm.setValue("limparArquivoPdf", false);
          hookForm.setValue("filePdf", f);

          onChange(f);
        }
      }
    },
    [saving, hookForm],
  );

  const handleChangeHistory = useCallback(() => {
    if (selectedDocumento) onChangeHistory();
  }, [selectedDocumento, onChangeHistory]);

  return (
    <Dialog
      open={open}
      slotProps={{
        paper: {
          component: "form",
          onSubmit: hookForm.handleSubmit(handleSubmit),
        },
      }}
      sx={{
        "& .MuiDialog-container": {
          "& .MuiPaper-root": {
            width: "100%",
            maxWidth: "65rem",
          },
        },
      }}
    >
      <DialogTitle>Cadastrar documento</DialogTitle>

      <DialogContent>
        <ModalLoading loading={saving}>
          <FormContainer title={"Informações"} arrangement="fullRow" gap="small">
            <>
              <DefaultFormRadioGroupInput
                name="statusArquivo"
                label="Status do Arquivo"
                options={statusOptions}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={"arquivo"}
                label={"Nome"}
                readOnly={saving}
                required={true}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name="versao"
                label="Versão"
                readOnly={saving}
                reactHookForm={hookForm}
              />

              <DefaultFormAutocomplete
                name="centroCusto"
                label="Centro de Custo"
                options={centroCustoDropdown}
                getOptionKey={(x) => x.id}
                getOptionLabel={(x) => x.title}
                reactHookForm={hookForm}
              />

              <DefaultFormDatePickerInput
                name="conferirAtualizacao"
                label="Conferir Atualização em"
                reactHookForm={hookForm}
              />

              <div className="w-full flex flex-row gap-3">
                <DefaultFormDatePickerInput
                  name="dataVencimento"
                  label="Data de Vencimento"
                  reactHookForm={hookForm}
                />

                <DefaultFormDatePickerInput
                  name="dataPublicacao"
                  label="Data de Publicação"
                  reactHookForm={hookForm}
                />

                <DefaultFormDatePickerInput
                  name="dataVigencia"
                  label="Data de Vigência"
                  reactHookForm={hookForm}
                />
              </div>

              <DefaultFormInput
                name="local1"
                label="Local 1"
                readOnly={saving}
                multiline={true}
                rows={5}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name="local2"
                label="Local 2"
                readOnly={saving}
                multiline={true}
                rows={5}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name="assunto"
                label="Assunto"
                readOnly={saving}
                multiline={true}
                rows={5}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name="ondeConsultar"
                label="Onde Consultar?"
                readOnly={saving}
                multiline={true}
                rows={5}
                reactHookForm={hookForm}
              />

              <div className="flex w-full">
                <Controller
                  name="file"
                  control={hookForm.control}
                  render={({ field }) =>
                    gedDocumento &&
                      gedDocumento.nome &&
                      gedDocumento.nome.length > 0 ? (
                      <div className="w-full flex flex-row justify-between items-center">
                        <span
                          className="cursor-pointer"
                          onMouseDown={handleDownloadFile}
                        >
                          {gedDocumento &&
                            gedDocumento.nome &&
                            gedDocumento.nome.length > 40
                            ? `${gedDocumento.nome.substring(0, 40)}...`
                            : gedDocumento.nome}
                        </span>
                        <IconButton onClick={handleDeleteFile}>
                          <DynamicIcon
                            nameIcon="DeleteForeverRounded"
                            size={25}
                            className="text-red-500"
                          />
                        </IconButton>
                      </div>
                    ) : (
                      <PrimaryButton
                        component="label"
                        role={undefined}
                        variant="contained"
                        tabIndex={-1}
                        disabled={saving}
                        startIcon={
                          <DynamicIcon
                            nameIcon="Download"
                            size={25}
                            className="text-white"
                          />
                        }
                      >
                        {field.value
                          ? normalizeFileName(field.value?.name, 40)
                          : "Escolher arquivo"}
                        <VisuallyHiddenInput
                          type="file"
                          onChange={(e) => handleFileChange(e, field.onChange)}
                          multiple={false}
                        />
                      </PrimaryButton>
                    )
                  }
                />
              </div>

              <div className="flex w-full">
                <Controller
                  name="filePdf"
                  control={hookForm.control}
                  render={({ field }) =>
                    gedDocumento &&
                      gedDocumento.nomePdf &&
                      gedDocumento.nomePdf.length > 0 ? (
                      <div className="w-full flex flex-row justify-between items-center">
                        <span
                          className="cursor-pointer"
                          onMouseDown={handleDownloadPdf}
                        >
                          {gedDocumento &&
                            gedDocumento.nomePdf &&
                            gedDocumento.nomePdf.length > 40
                            ? `${gedDocumento.nomePdf.substring(0, 40)}...`
                            : gedDocumento.nomePdf}
                        </span>
                        <IconButton onClick={handleDeletePdf}>
                          <DynamicIcon
                            nameIcon="DeleteForeverRounded"
                            size={25}
                            className="text-red-500"
                          />
                        </IconButton>
                      </div>
                    ) : (
                      <PrimaryButton
                        component="label"
                        role={undefined}
                        variant="contained"
                        tabIndex={-1}
                        disabled={saving}
                        startIcon={
                          <DynamicIcon
                            nameIcon="Download"
                            size={25}
                            className="text-white"
                          />
                        }
                      >
                        {field.value
                          ? normalizeFileName(field.value?.name, 40)
                          : "Escolher arquivo PDF"}
                        <VisuallyHiddenInput
                          type="file"
                          accept=".pdf"
                          onChange={(e) => handlePdfChange(e, field.onChange)}
                          multiple={false}
                        />
                      </PrimaryButton>
                    )
                  }
                />
              </div>
            </>
          </FormContainer>
        </ModalLoading>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div>
            {isNotNullOrUndefined(selectedDocumento) && (
              <PrimaryButton buttonColor="yellow" onClick={handleChangeHistory}>
                <span className="text-xs">Historico de alterações</span>
              </PrimaryButton>
            )}
          </div>

          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={handleClose}
              >
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton type="submit" buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
};
